import { useEffect, useState, useContext } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle, XCircle, Mail } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MagicLinkVerify() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useContext(AuthContext);
  const [status, setStatus] = useState('verifying');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setErrorMessage('Invalid magic link. No token found.');
      return;
    }

    const verifyToken = async () => {
      try {
        const response = await axios.post(`${API}/auth/magic-link/verify`, { token });
        if (response.data.session_token) {
          localStorage.setItem('session_token', response.data.session_token);
        }
        setStatus('success');
        toast.success('Signed in successfully!');
        // Give a moment for the toast to show, then redirect
        setTimeout(async () => {
          await checkAuth();
          navigate('/dashboard', { replace: true });
        }, 1000);
      } catch (error) {
        setStatus('error');
        setErrorMessage(
          error.response?.data?.detail ||
          'This link is invalid or has expired. Please request a new sign-in link.'
        );
      }
    };

    verifyToken();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-green-50 p-4">
      <Card className="w-full max-w-md shadow-2xl border-0" data-testid="magic-link-verify-card">
        <CardHeader className="space-y-4 text-center">
          <div className="flex justify-center mb-4">
            <img
              src="https://customer-assets.emergentagent.com/job_action-portal/artifacts/0jx7uwn8_HLRA_logo_non_transparent.jpeg"
              alt="HLRA Logo"
              className="h-24 w-auto"
            />
          </div>
          <CardTitle className="text-2xl font-bold">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-green-600">
              HLRA Action Portal
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {status === 'verifying' && (
            <div className="flex flex-col items-center space-y-4 py-8" data-testid="magic-link-verifying">
              <Loader2 className="h-10 w-10 animate-spin text-blue-500" />
              <p className="text-gray-600 font-medium">Verifying your sign-in link...</p>
              <p className="text-sm text-gray-400">Please wait a moment</p>
            </div>
          )}

          {status === 'success' && (
            <div className="flex flex-col items-center space-y-4 py-8" data-testid="magic-link-success">
              <CheckCircle className="h-10 w-10 text-green-500" />
              <p className="text-gray-700 font-medium">Signed in successfully!</p>
              <p className="text-sm text-gray-400">Redirecting to dashboard...</p>
            </div>
          )}

          {status === 'error' && (
            <div className="flex flex-col items-center space-y-4 py-6" data-testid="magic-link-error">
              <XCircle className="h-10 w-10 text-red-500" />
              <p className="text-gray-700 font-medium text-center">{errorMessage}</p>
              <div className="flex flex-col gap-3 w-full pt-2">
                <Button
                  onClick={() => navigate('/login?tab=magic-link')}
                  className="w-full bg-gradient-to-r from-blue-500 to-green-500 hover:from-blue-600 hover:to-green-600"
                  data-testid="request-new-link-btn"
                >
                  <Mail className="mr-2 h-4 w-4" />
                  Request a New Link
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/login')}
                  className="w-full"
                  data-testid="back-to-login-btn"
                >
                  Back to Login
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
